import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  getOptimizedImageUrl,
  ImagePresets,
} from "@/lib/utils/supabaseImageTransform";
import { fetchArticlesWithCategoryCounts } from "@/services/newsService";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// NewsCard Component
function NewsCard({ article, view }) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (view === "list") {
    return (
      <Link to={`/news/${article.slug}`}>
        <article className="flex flex-col md:flex-row gap-6 p-6 rounded-lg bg-gray-100 dark:bg-[#111111] hover:bg-gray-200 dark:hover:bg-[#1a1a1a] transition-colors border border-gray-200 dark:border-gray-800">
          <div className="md:w-1/3 flex-shrink-0">
            <img
              src={getOptimizedImageUrl(
                article.image?.url ||
                  article.imageUrl ||
                  "/placeholder.svg?height=200&width=300",
                ImagePresets.preview
              )}
              alt={article.image?.alt || article.title}
              className="w-full h-48 md:h-32 object-cover rounded-lg"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="md:w-2/3 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full">
                  {article.category}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(article.date)}
                </span>
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                {article.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                {article.excerpt}
              </p>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full overflow-hidden">
                  <img
                    src={getOptimizedImageUrl(
                      "/blog/jack-image.jpeg",
                      ImagePresets.avatar
                    )}
                    alt={article.author?.name || "Author"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {article.author?.name || "Anonymous"}
                </span>
              </div>
              <span className="text-sm text-gray-500">{article.readTime}</span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link to={`/news/${article.slug}`}>
      <article className="group cursor-pointer">
        <div className="bg-gray-100 dark:bg-[#111111] rounded-lg overflow-hidden hover:bg-gray-200 dark:hover:bg-[#1a1a1a] transition-colors border border-gray-200 dark:border-gray-800">
          <div className="aspect-video overflow-hidden">
            <img
              src={getOptimizedImageUrl(
                article.image?.url ||
                  article.imageUrl ||
                  "/placeholder.svg?height=200&width=300",
                ImagePresets.preview
              )}
              alt={article.image?.alt || article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-3 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full">
                {article.category}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(article.date)}
              </span>
            </div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              {article.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
              {article.excerpt}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full overflow-hidden">
                  <img
                    src={getOptimizedImageUrl(
                      "/blog/jack-image.jpeg",
                      ImagePresets.avatar
                    )}
                    alt={article.author?.name || "Author"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {article.author?.name || "Anonymous"}
                </span>
              </div>
              <span className="text-sm text-gray-500">{article.readTime}</span>
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [view, setView] = useState("grid");
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [sortBy, setSortBy] = useState("newest");

  // Fetch articles and categories in a single API call
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      const {
        articles: data,
        categories: cats,
        error: fetchError,
      } = await fetchArticlesWithCategoryCounts({
        category: activeCategory === "all" ? undefined : activeCategory,
        tags: selectedTopics.length > 0 ? selectedTopics : undefined,
        years: selectedYears.length > 0 ? selectedYears : undefined,
        sortBy: sortBy,
      });

      if (fetchError) {
        console.error("Error loading data:", fetchError);
        setError("Failed to load articles. Please try again later.");
        setArticles([]);
        // Fallback to default categories
        if (categories.length === 0) {
          setCategories([
            { id: "all", label: "All" },
            { id: "company", label: "Company" },
            { id: "research", label: "Research" },
            { id: "product", label: "Product" },
            { id: "safety", label: "Safety" },
          ]);
        }
      } else {
        setArticles(data);
        // Only update categories on initial load to preserve counts
        if (categories.length === 0) {
          setCategories(cats);
        }
      }

      setIsLoading(false);
    };

    loadData();
  }, [activeCategory, selectedTopics, selectedYears, sortBy]);

  const topicOptions = [
    "Alignment",
    "API Platform",
    "ChatGPT",
    "Community",
    "Culture & Careers",
    "Events",
    "Framework",
    "Research",
    "Safety",
    "Security",
  ];
  const yearOptions = ["2025", "2024", "2023", "2022", "2021", "2020", "2019"];

  const handleTopicChange = (topic) => {
    const newSelectedTopics = selectedTopics.includes(topic)
      ? selectedTopics.filter((t) => t !== topic)
      : [...selectedTopics, topic];
    setSelectedTopics(newSelectedTopics);
  };

  const handleYearChange = (year) => {
    const newSelectedYears = selectedYears.includes(year)
      ? selectedYears.filter((y) => y !== year)
      : [...selectedYears, year];
    setSelectedYears(newSelectedYears);
  };

  const clearFilters = () => {
    setSelectedTopics([]);
    setSelectedYears([]);
  };

  return (
    <>
      <Helmet>
        <title>
          News & Updates | Artifact - Latest AI Assistant Developments
        </title>
        <meta
          name="description"
          content="Stay updated with the latest news, product updates, research insights, and AI developments from Artifact. Discover new features, AI model integrations, and industry insights."
        />

        {/* Keywords */}
        <meta
          name="keywords"
          content="Artifact news, AI assistant updates, ChatGPT news, Claude AI updates, AI research, product announcements, AI industry insights, technology blog"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://artifact.chat/news" />

        {/* Open Graph Meta Tags */}
        <meta
          property="og:title"
          content="News & Updates | Artifact - Latest AI Assistant Developments"
        />
        <meta
          property="og:description"
          content="Stay updated with the latest news, product updates, research insights, and AI developments from Artifact. Discover new features, AI model integrations, and industry insights."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://artifact.chat/news" />
        <meta
          property="og:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta property="og:site_name" content="Artifact" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="News & Updates | Artifact - Latest AI Assistant Developments"
        />
        <meta
          name="twitter:description"
          content="Stay updated with the latest news, product updates, research insights, and AI developments from Artifact. Discover new features, AI model integrations, and industry insights."
        />
        <meta
          name="twitter:image"
          content="https://artifact.chat/assets/og-image.jpeg"
        />
        <meta name="twitter:site" content="@ArtifactAI" />

        {/* Additional Meta Tags */}
        <meta name="robots" content="index, follow" />

        {/* Structured Data for News/Blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Artifact News & Updates",
            description:
              "Stay updated with the latest news, product updates, research insights, and AI developments from Artifact.",
            url: "https://artifact.chat/news",
            publisher: {
              "@type": "Organization",
              name: "Artifact",
              url: "https://artifact.chat",
            },
            blogPost: articles.slice(0, 5).map((article) => ({
              "@type": "BlogPosting",
              headline: article.title,
              description: article.excerpt,
              url: `https://artifact.chat/news/${article.slug}`,
              datePublished: article.date,
              author: {
                "@type": "Person",
                name: article.author?.name || "Artifact Team",
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="bg-white dark:bg-black text-gray-900 dark:text-white" ref={containerRef}>
        {/* Hero Section */}
        <section className="relative py-8 sm:py-12 overflow-hidden">
          {/* Simple gradient background */}
          <div className="absolute inset-0 bg-gradient-to-b from-purple-50/50 dark:from-purple-950/20 to-transparent pointer-events-none" />

          {/* Hero Content */}
          <div className="container relative z-10 mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-foreground">
                Tips & Insights
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
                Learn how to get the most out of Artifact and AI.
              </p>
            </div>
          </div>
        </section>

        {/* Navigation Section */}
        <nav className="px-6 py-4 md:px-12 relative z-20 bg-white/80 dark:bg-black/80 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-800/50">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <Tabs
              value={activeCategory}
              onValueChange={setActiveCategory}
              className="w-full"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <TabsList className={cn(
                  "!bg-transparent !dark:bg-transparent p-0 h-auto space-x-2 overflow-x-auto pb-2 md:pb-0 border-0 transition-opacity",
                  isLoading && "opacity-70 pointer-events-none"
                )}>
                  {categories.map((category) => (
                    <TabsTrigger
                      key={category.id}
                      value={category.id}
                      className={cn(
                        "px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all",
                        "!bg-transparent !border-0 !shadow-none",
                        "!data-[state=active]:bg-transparent !data-[state=active]:text-gray-900 dark:!data-[state=active]:text-white !data-[state=active]:shadow-none",
                        "data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-500 dark:data-[state=inactive]:text-gray-400",
                        "hover:text-gray-700 dark:hover:text-gray-200 hover:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                      )}
                    >
                      {category.label}
                      {category.count && (
                        <span className="ml-1 text-xs opacity-70">
                          ({category.count})
                        </span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>

                <div className="flex items-center gap-2">
                  {/* Filter Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-lg bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/50 transition-colors",
                          (selectedTopics.length > 0 ||
                            selectedYears.length > 0) &&
                            "bg-blue-600/20 text-blue-600 dark:text-blue-400 border border-blue-600/30"
                        )}
                      >
                        <span>Filter</span>
                        {(selectedTopics.length > 0 ||
                          selectedYears.length > 0) && (
                          <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                            {selectedTopics.length + selectedYears.length}
                          </span>
                        )}
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2 4h12M4 8h8M6 12h4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-80 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                      align="end"
                    >
                      <div className="p-4">
                        <DropdownMenuLabel className="text-base font-medium text-gray-900 dark:text-white mb-4">
                          Filter
                        </DropdownMenuLabel>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Topic Filter */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Topic
                            </h4>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {topicOptions.map((topic) => (
                                <div
                                  key={topic}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={`topic-${topic}`}
                                    checked={selectedTopics.includes(topic)}
                                    onCheckedChange={() =>
                                      handleTopicChange(topic)
                                    }
                                    className="border-gray-400 dark:border-gray-600 data-[state=checked]:bg-gray-900 dark:data-[state=checked]:bg-white data-[state=checked]:border-gray-900 dark:data-[state=checked]:border-white"
                                  />
                                  <Label
                                    htmlFor={`topic-${topic}`}
                                    className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  >
                                    {topic}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Year Filter */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Year
                            </h4>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {yearOptions.map((year) => (
                                <div
                                  key={year}
                                  className="flex items-center space-x-2"
                                >
                                  <Checkbox
                                    id={`year-${year}`}
                                    checked={selectedYears.includes(year)}
                                    onCheckedChange={() =>
                                      handleYearChange(year)
                                    }
                                    className="border-gray-400 dark:border-gray-600 data-[state=checked]:bg-gray-900 dark:data-[state=checked]:bg-white data-[state=checked]:border-gray-900 dark:data-[state=checked]:border-white"
                                  />
                                  <Label
                                    htmlFor={`year-${year}`}
                                    className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                                  >
                                    {year}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {(selectedTopics.length > 0 ||
                          selectedYears.length > 0) && (
                          <>
                            <DropdownMenuSeparator className="my-3 border-gray-200 dark:border-gray-700" />
                            <button
                              onClick={clearFilters}
                              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors bg-transparent"
                            >
                              Clear all filters
                            </button>
                          </>
                        )}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Sort Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/50 transition-colors">
                        <span>Sort</span>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 3v10M4 9l4 4 4-4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-56 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                      align="end"
                    >
                      <DropdownMenuRadioGroup
                        value={sortBy}
                        onValueChange={setSortBy}
                      >
                        <DropdownMenuRadioItem
                          value="newest"
                          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50"
                        >
                          Newest → Oldest
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="oldest"
                          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50"
                        >
                          Oldest → Newest
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="alphabetical-az"
                          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50"
                        >
                          Alphabetical (A-Z)
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="alphabetical-za"
                          className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50"
                        >
                          Alphabetical (Z-A)
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* View Toggle */}
                  <button
                    onClick={() => setView(view === "grid" ? "list" : "grid")}
                    className="flex items-center gap-1 p-1 rounded-lg bg-transparent hover:bg-gray-200 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div
                      className={cn(
                        "p-2 rounded transition-colors",
                        view === "grid"
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "text-gray-600 dark:text-gray-300"
                      )}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1h6v6H1V1zm8 0h6v6H9V1zM1 9h6v6H1V9zm8 0h6v6H9V9z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </div>
                    <div
                      className={cn(
                        "p-2 rounded transition-colors",
                        view === "list"
                          ? "bg-gray-900 dark:bg-white text-white dark:text-black"
                          : "text-gray-600 dark:text-gray-300"
                      )}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1 1h14v3H1V1zm0 5h14v3H1V6zm0 5h14v3H1v-3z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </Tabs>
          </div>

          {/* Active Filters Display */}
          {(selectedTopics.length > 0 || selectedYears.length > 0) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Active filters:</span>
              {selectedTopics.map((topic) => (
                <span
                  key={topic}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600/20 text-blue-600 dark:text-blue-400 text-sm rounded-full border border-blue-600/30"
                >
                  {topic}
                  <button
                    onClick={() => handleTopicChange(topic)}
                    className="hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M9 3L3 9M3 3l6 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </span>
              ))}
              {selectedYears.map((year) => (
                <span
                  key={year}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-green-600/20 text-green-600 dark:text-green-400 text-sm rounded-full border border-green-600/30"
                >
                  {year}
                  <button
                    onClick={() => handleYearChange(year)}
                    className="hover:text-green-500 dark:hover:text-green-300 transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M9 3L3 9M3 3l6 6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </span>
              ))}
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors underline bg-transparent"
              >
                Clear all
              </button>
            </div>
          )}
        </nav>

        {/* Content */}
        <main className={cn(
          "px-6 py-8 md:px-12 bg-white dark:bg-black relative z-20",
          isLoading && articles.length === 0 && "min-h-[60vh]"
        )}>

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <div className="text-red-500 dark:text-red-400 mb-4">{error}</div>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Articles Grid */}
          {!error && (
            <>
              {!isLoading && articles.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400 text-lg">
                    {selectedTopics.length > 0 || selectedYears.length > 0
                      ? "No articles found matching the selected filters."
                      : "No articles found for this category."}
                  </p>
                  {(selectedTopics.length > 0 || selectedYears.length > 0) && (
                    <button
                      onClick={clearFilters}
                      className="mt-4 px-6 py-3 bg-transparent border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <div
                  className={cn(
                    "grid gap-6 animate-fadeIn",
                    view === "grid"
                      ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                      : "grid-cols-1"
                  )}
                >
                  {articles.map((article) => (
                    <NewsCard key={article.id} article={article} view={view} />
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
